import { calculateLoanTimeInMonths, SecLoansApplicationMetadata, SecLoansMethodOfRepayment } from '@shared/sec-loans'
import { isNumber, toNumber } from '@ui-common/utils/helpers'
import { testIsValidMonetaryValue } from '@ui-common/utils/validation'
import { FieldName } from 'react-hook-form'
import { IntlShape } from 'react-intl'
import { create, enforce, omitWhen, only, Suite, test } from 'vest'

import { MIN_CONSTANT_DURATION_LOAN_TIME_IN_YEARS } from '../../../../constants/constants'
import { PaymentPlanInfoFormData as FormData } from './PaymentPlanInfoStep'
import { getMaxConstantDurationLoanTimeYears, getMaxConstantPaymentLoanTimeYears } from './paymentPlanInfoStepUtils'

export const createFormValidator = (
  formatMessage: IntlShape['formatMessage'],
  metadata: SecLoansApplicationMetadata,
  totalLoanAmount?: number,
): Suite<string, string, (data: FormData, changedFields?: FieldName<FormData>[]) => void> => {
  const maxConstantDurationLoanTimeYears = getMaxConstantDurationLoanTimeYears(metadata.applicationType)
  const maxConstantPaymentLoanTimeYears = getMaxConstantPaymentLoanTimeYears(metadata.applicationType)

  return create((data, changedFields) => {
    only(changedFields)

    test('methodOfRepayment', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.methodOfRepayment).inside(Object.values(SecLoansMethodOfRepayment))
    })

    test('repaymentFreeMonthsAmount', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.repaymentFreeMonthsAmount).isBetween(0, 12)
    })

    omitWhen(data.methodOfRepayment === SecLoansMethodOfRepayment.ConstantAmount, () => {
      test('loanTimeInYears', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.loanTimeInYears).isNotEmpty()
      })

      test('loanTimeInYears', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.loanTimeInYears).isBetween(
          MIN_CONSTANT_DURATION_LOAN_TIME_IN_YEARS,
          maxConstantDurationLoanTimeYears,
        )
      })
    })

    omitWhen(data.methodOfRepayment !== SecLoansMethodOfRepayment.ConstantAmount, () => {
      testIsValidMonetaryValue('approximatedRepayment', data.approximatedRepayment, formatMessage)

      test(
        'approximatedRepayment',
        formatMessage(
          { id: 'sec-loans-validate-value-is-not-more-than-other-value' },
          {
            firstValue: formatMessage({
              id: 'sec-loans-payment-plan-monthly-repayment-approximation-without-euro-per-month',
            }),
            secondValue: formatMessage({ id: 'sec-loans-loans-and-credits-info-loan-credit-amount-form-label' }),
          },
        ),
        () => {
          enforce(toNumber(data.approximatedRepayment)).lessThan(totalLoanAmount)
        },
      )

      const approximatedRepayment = toNumber(data.approximatedRepayment)
      const estimatedLoanTimeInMonths =
        isNumber(totalLoanAmount) && isNumber(approximatedRepayment)
          ? calculateLoanTimeInMonths(totalLoanAmount, metadata.interestRate, approximatedRepayment)
          : undefined

      test(
        'approximatedRepayment',
        formatMessage(
          { id: 'sec-loans-payment-plan-validate-monthly-repayment-approximation-give-bigger' },
          { maxYears: maxConstantPaymentLoanTimeYears },
        ),
        () => {
          enforce(estimatedLoanTimeInMonths).isNotNaN()
          enforce(isNumber(estimatedLoanTimeInMonths)).isTruthy() // infinite check

          if (isNumber(estimatedLoanTimeInMonths)) {
            enforce(Math.ceil(estimatedLoanTimeInMonths)).lessThanOrEquals(maxConstantPaymentLoanTimeYears * 12)
          }
        },
      )
    })
  })
}
